/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { DEPARTMENTS, type Department, type Procedure, type Step } from "../shared/types";
import { WeekdayPicker } from "../shared/WeekdayPicker";
import { StepEditorRow } from "./StepEditorRow";
import { fromTemplate, PROCEDURE_TEMPLATES, type ProcedureTemplate } from "./templates";

type Props = {
  procedureId: number | null;
  /** Open on the schedule (from "Create schedule"). */
  focusSchedule?: boolean;
  onClose: (changed: boolean) => void;
};

const newStep = (): Step => ({ title: "", kind: "check", required: true, min_value: null, max_value: null, unit: null });

// Create or edit a procedure: name, department, schedule, steps. New ones can
// start from a template.
export const ProcedureEditor = defineComponent<Props>({
  name: "ProcedureEditor",
  props: ["procedureId", "focusSchedule", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(props.procedureId != null);
    const saving = ref(false);
    // New procedures first pick a template (or blank).
    const picking = ref(props.procedureId == null);
    const name = ref("");
    const description = ref("");
    const department = ref<Department>("kitchen");
    const days = ref<number[]>([]);
    const time = ref("");
    const active = ref(true);
    const steps = ref<Step[]>([newStep()]);

    onMounted(async () => {
      if (props.procedureId == null) return;
      try {
        const p = (await trpc.workday.procedures.get.query({ id: props.procedureId })) as Procedure;
        name.value = p.name;
        description.value = p.description ?? "";
        department.value = p.department;
        days.value = p.schedule_days;
        time.value = p.schedule_time ?? "";
        active.value = p.active;
        steps.value = p.steps.length ? p.steps : [newStep()];
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
        props.onClose(false);
      } finally {
        loading.value = false;
      }
    });

    const useTemplate = (tpl: ProcedureTemplate | null) => {
      if (tpl) {
        const f = fromTemplate(tpl, t);
        name.value = f.name;
        department.value = f.department;
        days.value = f.schedule_days;
        time.value = f.schedule_time ?? "";
        steps.value = f.steps;
      }
      picking.value = false;
    };

    const filled = computed(() => steps.value.filter((s) => s.title.trim()));
    const canSave = computed(() => !saving.value && name.value.trim() && filled.value.length > 0);

    const move = (i: number, delta: number) => {
      const next = [...steps.value];
      const [s] = next.splice(i, 1);
      next.splice(i + delta, 0, s);
      steps.value = next;
    };

    const save = async () => {
      saving.value = true;
      try {
        await trpc.workday.procedures.save.mutate({
          id: props.procedureId ?? undefined,
          name: name.value.trim(),
          description: description.value.trim() || null,
          department: department.value,
          schedule_days: days.value,
          schedule_time: days.value.length && time.value ? time.value : null,
          active: active.value,
          steps: filled.value.map((s) => ({
            id: s.id,
            title: s.title.trim(),
            kind: s.kind,
            required: s.required,
            min_value: s.min_value,
            max_value: s.max_value,
            unit: s.unit,
          })),
        });
        toast.showToast(t("workday.common.saved"), { type: "success" });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const renderTemplates = () => (
      <div class="space-y-3 p-6">
        <p class="text-sm text-base-content/70">{t("workday.procedures.start_from")}</p>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {PROCEDURE_TEMPLATES.map((tpl) => (
            <button
              key={tpl.key}
              class="flex items-start gap-3 rounded-xl border border-base-300 p-4 text-left transition hover:border-primary hover:bg-primary/5"
              onClick={() => useTemplate(tpl)}
            >
              <span class="material-symbols-outlined rounded-lg bg-primary/10 p-2 text-primary">{tpl.icon}</span>
              <span>
                <span class="block font-semibold">{t(`workday.tpl.${tpl.key}.name`)}</span>
                <span class="block text-xs text-base-content/60">
                  {t(`workday.department.${tpl.department}`)} ·{" "}
                  {t("workday.procedures.step_count").replace("{count}", String(tpl.steps.length))}
                </span>
              </span>
            </button>
          ))}
          <button
            class="flex items-center gap-3 rounded-xl border border-dashed border-base-300 p-4 text-left hover:border-primary"
            onClick={() => useTemplate(null)}
          >
            <span class="material-symbols-outlined rounded-lg bg-base-200 p-2">add</span>
            <span class="font-semibold">{t("workday.procedures.blank")}</span>
          </button>
        </div>
      </div>
    );

    const renderForm = () => (
      <div class="space-y-5 p-6">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <label class="block">
            <span class="mb-1 block text-sm font-medium">{t("workday.common.name")}</span>
            <input class="input input-bordered w-full" v-model={name.value} placeholder={t("workday.procedures.name_placeholder")} />
          </label>
          <label class="block">
            <span class="mb-1 block text-sm font-medium">{t("workday.common.department_label")}</span>
            <select class="select select-bordered w-full" value={department.value} onChange={(e: Event) => (department.value = (e.target as HTMLSelectElement).value as Department)}>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {t(`workday.department.${d}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label class="block">
          <span class="mb-1 block text-sm font-medium">{t("workday.procedures.instructions")}</span>
          <textarea class="textarea textarea-bordered w-full text-sm" rows={2} v-model={description.value} placeholder={t("workday.procedures.instructions_placeholder")} />
        </label>

        <div class={["rounded-xl border p-4", props.focusSchedule ? "border-primary bg-primary/5" : "border-base-300"].join(" ")}>
          <div class="mb-2 flex items-center gap-2 text-sm font-medium">
            <span class="material-symbols-outlined text-base">calendar_month</span>
            {t("workday.schedule.title")}
          </div>
          <WeekdayPicker days={days.value} onChange={(d: number[]) => (days.value = d)} />
          {days.value.length > 0 && (
            <label class="mt-3 flex items-center gap-2 text-sm">
              {t("workday.schedule.due_by")}
              <input type="time" class="input input-bordered input-sm" v-model={time.value} />
            </label>
          )}
          {days.value.length === 0 && <p class="mt-2 text-xs text-base-content/60">{t("workday.schedule.on_demand_help")}</p>}
        </div>

        <div>
          <div class="mb-2 flex items-center justify-between">
            <span class="text-sm font-medium">{t("workday.procedures.steps")}</span>
            <span class="text-xs text-base-content/50">{t("workday.procedures.step_count").replace("{count}", String(filled.value.length))}</span>
          </div>
          <div class="space-y-2">
            {steps.value.map((s, i) => (
              <StepEditorRow
                key={s.id ?? `new-${i}`}
                step={s}
                index={i}
                count={steps.value.length}
                onChange={(next: Step) => (steps.value = steps.value.map((x, j) => (j === i ? next : x)))}
                onMove={(d: number) => move(i, d)}
                onRemove={() => (steps.value = steps.value.filter((_, j) => j !== i))}
              />
            ))}
          </div>
          <button class="btn btn-ghost btn-sm mt-2" onClick={() => (steps.value = [...steps.value, newStep()])}>
            <span class="material-symbols-outlined text-base">add</span>
            {t("workday.procedures.add_step")}
          </button>
        </div>

        {props.procedureId != null && (
          <label class="flex items-center gap-2 text-sm">
            <input type="checkbox" class="toggle toggle-sm" v-model={active.value} />
            {t("workday.common.active_help")}
          </label>
        )}
      </div>
    );

    return () => (
      <LemmonModal
        title={props.procedureId ? t("workday.procedures.edit_title") : t("workday.procedures.new_title")}
        onClose={() => props.onClose(false)}
        renderButtons={() =>
          picking.value ? null : (
            <div class="border-t p-4">
              <div class="flex justify-between gap-2">
                <button class="btn btn-neutral" onClick={() => props.onClose(false)}>
                  {t("general.words.cancel")}
                </button>
                <button class="btn btn-primary" disabled={!canSave.value} onClick={save}>
                  {t("workday.common.save")}
                </button>
              </div>
            </div>
          )
        }
      >
        {loading.value ? (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        ) : picking.value ? (
          renderTemplates()
        ) : (
          renderForm()
        )}
      </LemmonModal>
    );
  },
});
